/**
 * Registry of recently-released features used to drive "New" badges in the UI.
 *
 * A feature is considered "new" for a user while it is within its TTL window
 * (releaseDate + ttlDays) AND the user has not yet dismissed it (dismissal
 * happens automatically on first interaction — see `useIsFeatureNew`).
 *
 * To flag a new feature:
 *   1. Add an entry here with a stable `id` and the `releaseDate`.
 *   2. Pass `featureId` to the relevant sidebar `MenuItem` (and include the id
 *      in a group list such as `PLATFORM_FEATURE_IDS` if it should roll up to an
 *      accordion).
 * The badge self-expires after the TTL, so there is no cleanup to remember.
 */
import { CUSTOM_EMAILS_FLAG } from '../constants/emailTemplates';

export interface FeatureDescriptor {
	/** Stable identifier — also used as the localStorage dismissal key. */
	id: string;
	/** ISO date (YYYY-MM-DD) the feature was released. */
	releaseDate: string;
	/** Days the "New" badge should persist from `releaseDate`. Defaults to 30. */
	ttlDays?: number;
	/**
	 * Feature flag that reveals this feature, if it ships dark.
	 *
	 * When set, the badge window runs from the moment the flag was switched on
	 * rather than from `releaseDate` — because a feature released in August and
	 * enabled in November is new to users in November, and a date-only window
	 * would have expired before anyone could see it.
	 */
	flag?: string;
}

/** Industry-standard default lifespan for a "New" badge. */
export const DEFAULT_FEATURE_TTL_DAYS = 30;

export const FEATURE_REGISTRY: FeatureDescriptor[] = [
	{ id: 'aiAssistant', releaseDate: '2026-06-09' },
	{ id: 'caseManagement', releaseDate: '2026-06-09' },
	{ id: 'appearance', releaseDate: '2026-06-09' },
	{ id: 'featureFlags', releaseDate: '2026-06-09' },
	{ id: 'system', releaseDate: '2026-06-13' },
	{ id: 'legal', releaseDate: '2026-06-18' },
	{ id: 'faqBuilder', releaseDate: '2026-06-19' },
	{ id: 'themes', releaseDate: '2026-06-10' },
	{ id: 'superchat', releaseDate: '2026-08-25', flag: 'superchat-widget-enabled' },
	{ id: 'emailTemplates', releaseDate: '2026-09-29', flag: CUSTOM_EMAILS_FLAG }
];

/** Feature ids surfaced under the Platform accordion (drives its roll-up dot). */
export const PLATFORM_FEATURE_IDS = [
	'aiAssistant',
	'caseManagement',
	'appearance',
	'featureFlags',
	'system',
	'legal',
	'faqBuilder',
	'emailTemplates'
];

export const getFeature = (id: string): FeatureDescriptor | undefined =>
	FEATURE_REGISTRY.find((feature) => feature.id === id);
